import { Component, effect, input, signal } from '@angular/core';
import { StoreService } from '../../../store/store.service';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';
declare let google: any;

@Component({
  standalone: true,
  imports: [],
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: 'map.component.scss',
})
export class MapComponent {
  public coordinates = input.required<{ lat: number; lng: number }>();
  public zoomLevel = input.required<number>();
  public showMap = input.required<boolean>();
  public hotelName = input.required<string>();

  public map: any;
  public readonly mapLoading = signal<boolean>(false);
  public readonly mapError = signal<string | null>(null);

  constructor(
    private readonly googleMapsLoaderService: GoogleMapsLoaderService,
    public readonly _storeSvc: StoreService,
  ) {
    effect(() => {
      if (this.showMap()) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          this.initMap();
        }, 0);
      }
    });
    effect(() => {
      if (this.coordinates()) {
        this.initMap();
      }
    });
  }

  public createMapMarkerIcon = () => {
    // Create an SVG string for the custom marker icon
    const svgIcon = `
      <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
        <path fill="var(--global-text-primary-text-color)"
        d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 
           168.3 307.2c12.3 15.3 35.1 15.3 47.4 0z
           M192 256c-35.3 0-64-28.7-64-64s28.7-64 64-64s64 28.7 64 64s-28.7 64-64 64z"/>
      </svg>
    `;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgIcon),
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 40),
    };
  };

  public initMap = async () => {
    // Load Google Maps API first
    this.mapLoading.set(true);
    this.mapError.set(null);

    try {
      await this.googleMapsLoaderService.loadGoogleMaps();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load map';
      this.mapError.set(errorMsg);
      this.mapLoading.set(false);
      return;
    }

    this.mapLoading.set(false);
    // Initialize the map
    try {
      // Check if map element exists
      const mapElement = document.getElementById('map');
      if (!mapElement) {
        throw new Error('Map container element not found');
      }

      // Verify Google Maps API is fully loaded
      if (typeof google === 'undefined' || !google.maps || !google.maps.Map) {
        throw new Error('Google Maps API not fully loaded');
      }

      const zoom = this.zoomLevel() || 15;
      const position = { lat: this.coordinates().lat, lng: this.coordinates().lng };

      // Use traditional Google Maps API (v3)
      this.map = new google.maps.Map(mapElement, {
        zoom,
        center: position,
        disableDefaultUI: true,
      });

      // Create custom map marker icon
      const customIcon = this.createMapMarkerIcon();

      const marker = new google.maps.Marker({
        map: this.map,
        position: position,
        icon: customIcon,
      });

      const infowindow = new google.maps.InfoWindow({
        content: `<span class="fw-medium">${this.hotelName()}</span>`,
      });

      google.maps.event.addListener(marker, 'click', () => {
        infowindow.open(this.map, marker);
      });

      infowindow.open(this.map, marker);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to initialize map';
      this.mapError.set(errorMsg);
    }
  };
}
